/** 買い物セッションの TanStack Query フック */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { shoppingSessionsApi } from "../lib/api";
import { TODOS_KEY } from "./use-todos";

export const SESSIONS_KEY = ["shopping-sessions"] as const;

const sessionDetailKey = (id: string) => [...SESSIONS_KEY, id] as const;

/** アクティブな買い物セッション一覧を取得する */
export const useActiveSessions = () => {
  return useQuery({
    queryKey: SESSIONS_KEY,
    queryFn: async () => {
      const res = await shoppingSessionsApi.active.$get();
      if (!res.ok) throw new Error("Failed to fetch sessions");
      const json = await res.json();
      return json.data;
    },
  });
};

/** 買い物セッションを ID で取得する（アイテム込み） */
export const useSession = (id: string) => {
  return useQuery({
    queryKey: sessionDetailKey(id),
    queryFn: async () => {
      const res = await shoppingSessionsApi[":id"].$get({
        param: { id },
      });
      if (!res.ok) throw new Error("Failed to fetch session");
      const json = await res.json();
      return json.data;
    },
    enabled: !!id,
  });
};

/** 買い物セッションを作成する */
export const useCreateSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      name?: string;
      venues: string[];
      items: { todoId: string; venueName: string }[];
    }) => {
      const res = await shoppingSessionsApi.index.$post({ json: input });
      if (!res.ok) throw new Error("Failed to create session");
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
    onError: (err) => {
      toast.error("セッションの作成に失敗しました");
    },
  });
};

/** 買い物セッションを完了する */
export const useCompleteSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await shoppingSessionsApi[":id"].complete.$patch({
        param: { id },
      });
      if (!res.ok) throw new Error("Failed to complete session");
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
      queryClient.invalidateQueries({ queryKey: TODOS_KEY });
      toast.success("買い物を完了しました");
    },
    onError: (err) => {
      toast.error("セッションの完了に失敗しました");
    },
  });
};

/** 買い物セッションを削除する */
export const useDeleteSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await shoppingSessionsApi[":id"].$delete({
        param: { id },
      });
      if (!res.ok) throw new Error("Failed to delete session");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
      toast.success("買い物セッションを破棄しました");
    },
    onError: (err) => {
      toast.error("セッションの削除に失敗しました");
    },
  });
};

/** セッションにアイテムを追加する */
export const useAddSessionItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sessionId,
      todoId,
      venueName,
    }: {
      sessionId: string;
      todoId: string;
      venueName: string;
    }) => {
      const res = await shoppingSessionsApi[":id"].items.$post({
        param: { id: sessionId },
        json: { todoId, venueName },
      });
      if (!res.ok) throw new Error("Failed to add item");
      const json = await res.json();
      return json.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: sessionDetailKey(variables.sessionId),
      });
    },
    onError: (err) => {
      toast.error("アイテムの追加に失敗しました");
    },
  });
};

/** セッションからアイテムを除外する */
export const useRemoveSessionItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sessionId,
      todoId,
    }: {
      sessionId: string;
      todoId: string;
    }) => {
      const res = await shoppingSessionsApi[":id"].items[":todoId"].$delete({
        param: { id: sessionId, todoId },
      });
      if (!res.ok) throw new Error("Failed to remove item");
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: sessionDetailKey(variables.sessionId),
      });
    },
    onError: (err) => {
      toast.error("アイテムの除外に失敗しました");
    },
  });
};
